import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Favorite,
  TrendingUp,
  Assessment,
  Warning,
  CheckCircle,
  AccessTime,
  MonitorHeart,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const BloodPressurePage = () => {
  const { token } = useSelector(state => state.auth);
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    systolic: '',
    diastolic: '',
    pulse: '',
    measurementDate: new Date(),
    timeOfDay: 'morning',
    position: 'sitting',
    arm: 'left',
    cuffSize: 'medium',
    notes: '',
    symptoms: {
      headache: false,
      dizziness: false,
      chestPain: false,
      shortnessOfBreath: false,
      palpitations: false,
      fatigue: false,
    },
    lifestyle: {
      recentActivity: 'resting',
      stressLevel: 5,
      caffeineIntake: false,
      medicationTaken: false,
    },
  });

  // Fetch readings
  const fetchReadings = async () => {
    try {
      const response = await fetch('/api/blood-pressure', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch readings');
      }
      
      const data = await response.json();
      setReadings(data.readings || []);
    } catch (err) {
      setError('Failed to load blood pressure readings');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReadings();
  }, [token]);

  // Submit new reading
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const submitData = {
        systolic: parseInt(formData.systolic),
        diastolic: parseInt(formData.diastolic),
        pulse: parseInt(formData.pulse),
        measurementDate: formData.measurementDate,
        timeOfDay: formData.timeOfDay,
        conditions: {
          position: formData.position,
          arm: formData.arm,
          cuffSize: formData.cuffSize,
        },
        symptoms: formData.symptoms,
        lifestyle: {
          ...formData.lifestyle,
          stressLevel: parseInt(formData.stressLevel),
        },
        notes: {
          userNote: formData.notes,
        },
      };

      const response = await fetch('/api/blood-pressure', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(submitData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save reading');
      }

      setSuccess('Blood pressure reading saved successfully!');
      setShowAddForm(false);
      resetForm();
      fetchReadings();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      systolic: '',
      diastolic: '',
      pulse: '',
      measurementDate: new Date(),
      timeOfDay: 'morning',
      position: 'sitting',
      arm: 'left',
      cuffSize: 'medium',
      notes: '',
      symptoms: {
        headache: false,
        dizziness: false,
        chestPain: false,
        shortnessOfBreath: false,
        palpitations: false,
        fatigue: false,
      },
      lifestyle: {
        recentActivity: 'resting',
        stressLevel: 5,
        caffeineIntake: false,
        medicationTaken: false,
      },
    });
  };

  const handleFormChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const getBloodPressureStatus = (systolic, diastolic) => {
    if (systolic >= 180 || diastolic >= 120) {
      return { status: 'Hypertensive Crisis', color: 'error', severity: 'high' };
    }
    if (systolic >= 140 || diastolic >= 90) {
      return { status: 'High Stage 2', color: 'error', severity: 'high' };
    }
    if (systolic >= 130 || diastolic >= 80) {
      return { status: 'High Stage 1', color: 'warning', severity: 'medium' };
    }
    if (systolic >= 120 && diastolic < 80) {
      return { status: 'Elevated', color: 'warning', severity: 'medium' };
    }
    return { status: 'Normal', color: 'success', severity: 'low' };
  };

  const formatChartData = () => {
    return readings.slice(-30).map(reading => ({
      date: new Date(reading.measurementDate).toLocaleDateString(),
      systolic: reading.systolic,
      diastolic: reading.diastolic,
      pulse: reading.pulse,
    }));
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Blood Pressure Tracking
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Monitor your blood pressure trends and maintain optimal cardiovascular health
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Actions */}
      <Box mb={4}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setShowAddForm(true)}
          size="large"
        >
          Record New Reading
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Chart */}
        <Grid item xs={12}>
          <Card>
            <CardHeader 
              title="Blood Pressure Trends (Last 30 Readings)"
              avatar={<TrendingUp color="primary" />}
            />
            <CardContent>
              {readings.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={formatChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="systolic" 
                      stroke="#f44336" 
                      name="Systolic" 
                      strokeWidth={2}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="diastolic" 
                      stroke="#2196f3" 
                      name="Diastolic" 
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Typography variant="body1" color="text.secondary" textAlign="center">
                  No readings available yet. Start by recording your first measurement!
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Readings */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader 
              title="Recent Readings"
              avatar={<MonitorHeart color="primary" />}
            />
            <CardContent>
              {readings.length > 0 ? (
                <List>
                  {readings.slice(0, 10).map((reading, index) => {
                    const bpStatus = getBloodPressureStatus(reading.systolic, reading.diastolic);
                    return (
                      <React.Fragment key={reading._id}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="h6">
                                  {reading.systolic}/{reading.diastolic}
                                </Typography>
                                <Chip 
                                  label={bpStatus.status} 
                                  color={bpStatus.color}
                                  size="small"
                                />
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  <Favorite color="error" sx={{ fontSize: 16, mr: 0.5 }} />
                                  Pulse: {reading.pulse} bpm
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  <AccessTime sx={{ fontSize: 16, mr: 0.5 }} />
                                  {new Date(reading.measurementDate).toLocaleString()}
                                </Typography>
                                {reading.notes?.userNote && (
                                  <Typography variant="body2" color="text.secondary">
                                    Note: {reading.notes.userNote}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < readings.length - 1 && index < 9 && <Divider />}
                      </React.Fragment>
                    );
                  })}
                </List>
              ) : (
                <Typography variant="body1" color="text.secondary" textAlign="center">
                  No readings recorded yet.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Stats */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader 
              title="Quick Stats"
              avatar={<Assessment color="primary" />}
            />
            <CardContent>
              {readings.length > 0 ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Readings: {readings.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Last Reading: {new Date(readings[0]?.measurementDate).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Average: {Math.round(readings.reduce((sum, r) => sum + r.systolic, 0) / readings.length)}/
                    {Math.round(readings.reduce((sum, r) => sum + r.diastolic, 0) / readings.length)}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Start tracking to see your stats
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Reading Dialog */}
      <Dialog 
        open={showAddForm} 
        onClose={() => setShowAddForm(false)}
        maxWidth="md"
        fullWidth
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle>Record New Blood Pressure Reading</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Basic Measurements */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Measurements
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Systolic (mmHg)"
                  type="number"
                  value={formData.systolic}
                  onChange={(e) => handleFormChange('systolic', e.target.value)}
                  required
                  fullWidth
                  inputProps={{ min: 60, max: 300 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Diastolic (mmHg)"
                  type="number"
                  value={formData.diastolic}
                  onChange={(e) => handleFormChange('diastolic', e.target.value)}
                  required
                  fullWidth
                  inputProps={{ min: 30, max: 200 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Pulse (bpm)"
                  type="number"
                  value={formData.pulse}
                  onChange={(e) => handleFormChange('pulse', e.target.value)}
                  required
                  fullWidth
                  inputProps={{ min: 30, max: 220 }}
                />
              </Grid>

              {/* Date and Time */}
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    label="Measurement Date & Time"
                    value={formData.measurementDate}
                    onChange={(date) => handleFormChange('measurementDate', date)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>

              {/* Conditions */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Measurement Conditions
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Time of Day</InputLabel>
                  <Select
                    value={formData.timeOfDay}
                    onChange={(e) => handleFormChange('timeOfDay', e.target.value)}
                    label="Time of Day"
                  >
                    <MenuItem value="morning">Morning</MenuItem>
                    <MenuItem value="afternoon">Afternoon</MenuItem>
                    <MenuItem value="evening">Evening</MenuItem>
                    <MenuItem value="night">Night</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Position</InputLabel>
                  <Select
                    value={formData.position}
                    onChange={(e) => handleFormChange('position', e.target.value)}
                    label="Position"
                  >
                    <MenuItem value="sitting">Sitting</MenuItem>
                    <MenuItem value="standing">Standing</MenuItem>
                    <MenuItem value="lying">Lying Down</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Arm Used</InputLabel>
                  <Select
                    value={formData.arm}
                    onChange={(e) => handleFormChange('arm', e.target.value)}
                    label="Arm Used"
                  >
                    <MenuItem value="left">Left</MenuItem>
                    <MenuItem value="right">Right</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Cuff Size</InputLabel>
                  <Select
                    value={formData.cuffSize}
                    onChange={(e) => handleFormChange('cuffSize', e.target.value)}
                    label="Cuff Size"
                  >
                    <MenuItem value="small">Small</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="large">Large</MenuItem>
                    <MenuItem value="extra-large">Extra Large</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Symptoms */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Symptoms (if any)
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(formData.symptoms).map(([symptom, checked]) => (
                    <Grid item xs={12} sm={6} key={symptom}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checked}
                            onChange={(e) => handleFormChange(`symptoms.${symptom}`, e.target.checked)}
                          />
                        }
                        label={symptom.charAt(0).toUpperCase() + symptom.slice(1).replace(/([A-Z])/g, ' $1')}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              {/* Lifestyle */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Lifestyle Factors
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Recent Activity</InputLabel>
                  <Select
                    value={formData.lifestyle.recentActivity}
                    onChange={(e) => handleFormChange('lifestyle.recentActivity', e.target.value)}
                    label="Recent Activity"
                  >
                    <MenuItem value="resting">Resting</MenuItem>
                    <MenuItem value="light_activity">Light Activity</MenuItem>
                    <MenuItem value="moderate_activity">Moderate Activity</MenuItem>
                    <MenuItem value="intense_activity">Intense Activity</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Stress Level (1-10)"
                  type="number"
                  value={formData.lifestyle.stressLevel}
                  onChange={(e) => handleFormChange('lifestyle.stressLevel', e.target.value)}
                  fullWidth
                  inputProps={{ min: 1, max: 10 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.lifestyle.caffeineIntake}
                      onChange={(e) => handleFormChange('lifestyle.caffeineIntake', e.target.checked)}
                    />
                  }
                  label="Had caffeine in last 2 hours"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.lifestyle.medicationTaken}
                      onChange={(e) => handleFormChange('lifestyle.medicationTaken', e.target.checked)}
                    />
                  }
                  label="Took medication today"
                />
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <TextField
                  label="Additional Notes (optional)"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  fullWidth
                  inputProps={{ maxLength: 500 }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : <Add />}
            >
              {submitting ? 'Saving...' : 'Save Reading'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default BloodPressurePage; 